import { TRUST_GRAPH_SWATCH_CLASSES } from '@/lib/constants';
import type { TrustGraphLegendItem } from '@uncain/shared';

export function TrustGraphLegend({
	legend,
	compact = false,
}: { legend: readonly TrustGraphLegendItem[]; compact?: boolean }) {
	if (compact) {
		return (
			<div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1 py-2 text-[11px] text-muted-foreground">
				{legend.map((item) => (
					<span
						key={item.key}
						className="inline-flex items-center gap-1.5"
						title={item.description ?? undefined}
					>
						<ShapeSwatch colorToken={item.colorToken} shape={item.shape} size={10} />
						{item.label}
					</span>
				))}
			</div>
		);
	}

	return (
		<div className="rounded-2xl border border-border/70 bg-card p-4">
			<h3 className="text-sm font-semibold text-foreground">Legend</h3>
			<div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
				{legend.map((item) => (
					<div
						key={item.key}
						className="flex items-center gap-2.5 rounded-lg border border-border/50 bg-muted/15 px-2.5 py-2"
					>
						<ShapeSwatch colorToken={item.colorToken} shape={item.shape} size={14} />
						<div className="min-w-0">
							<div className="text-xs font-medium text-foreground">{item.label}</div>
							{item.description && (
								<div className="truncate text-[10px] leading-4 text-muted-foreground">
									{item.description}
								</div>
							)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

/** CSS color value for a design-token string like `var(--color-primary)`. */
function resolveColor(colorToken: string): string {
	const mapping = TRUST_GRAPH_SWATCH_CLASSES[colorToken];
	if (!mapping) return '#94a3b8';
	// Extract the CSS variable from the Tailwind class, e.g. 'bg-[var(--color-primary)]' → 'var(--color-primary)'
	const match = /\[(.+)\]/.exec(mapping);
	return match ? (match[1] ?? colorToken) : colorToken;
}

/**
 * SVG-based shape swatch that renders the actual Cytoscape node shape
 * so the legend matches the graph at any size.
 */
function ShapeSwatch({
	colorToken,
	shape,
	size,
}: { colorToken: string; shape: string | null; size: number }) {
	const fill = resolveColor(colorToken);
	const half = size / 2;
	return (
		<svg
			width={size}
			height={size}
			viewBox={`0 0 ${String(size)} ${String(size)}`}
			className="shrink-0"
			aria-hidden="true"
		>
			<ShapePath shape={shape} size={size} half={half} fill={fill} />
		</svg>
	);
}

function ShapePath({
	shape,
	size,
	half,
	fill,
}: { shape: string | null; size: number; half: number; fill: string }) {
	switch (shape) {
		case 'roundrectangle':
			return <rect x={0} y={0} width={size} height={size} rx={2} fill={fill} />;
		case 'diamond':
			return (
				<polygon
					points={`${String(half)},0 ${String(size)},${String(half)} ${String(half)},${String(size)} 0,${String(half)}`}
					fill={fill}
				/>
			);
		case 'hexagon': {
			const q = size * 0.25;
			return (
				<polygon
					points={`${String(q)},0 ${String(size - q)},0 ${String(size)},${String(half)} ${String(size - q)},${String(size)} ${String(q)},${String(size)} 0,${String(half)}`}
					fill={fill}
				/>
			);
		}
		case 'pentagon': {
			const px = (i: number) => half + half * Math.sin((2 * Math.PI * i) / 5 - Math.PI / 2);
			const py = (i: number) => half - half * Math.cos((2 * Math.PI * i) / 5 - Math.PI / 2);
			const pts = Array.from(
				{ length: 5 },
				(_, i) => `${String(Math.round(px(i)))},${String(Math.round(py(i)))}`,
			).join(' ');
			return <polygon points={pts} fill={fill} />;
		}
		case 'tag':
			return (
				<polygon
					points={`0,0 ${String(size * 0.7)},0 ${String(size)},${String(half)} ${String(size * 0.7)},${String(size)} 0,${String(size)}`}
					fill={fill}
				/>
			);
		default:
			return <circle cx={half} cy={half} r={half} fill={fill} />;
	}
}
